import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileDownload as ExportIcon,
} from '@mui/icons-material';
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRowParams,
} from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchTools, deleteTool } from '../../store/slices/inventorySlice';
import { fetchMachines } from '../../store/slices/machinesSlice';
import { Tool } from '../../types/inventory';
import { StatusBadge } from '../../components/inventory/StatusBadge';

export const ToolsOverviewPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { items: tools, isLoading } = useAppSelector((state) => state.inventory.tools);
  const { machines } = useAppSelector((state) => state.machines);
  const { user } = useAppSelector((state) => state.auth);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [conditionFilter, setConditionFilter] = useState<string>('all');
  const [machineFilter, setMachineFilter] = useState<string>('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Tool | null>(null);

  const canEdit = user?.role === 'admin';

  useEffect(() => {
    dispatch(fetchMachines({ page: 1, limit: 1000 }));
  }, [dispatch]);

  useEffect(() => {
    if (machines.length > 0) {
      machines.forEach(machine => {
        dispatch(fetchTools({ machineId: machine.id }));
      });
    }
  }, [dispatch, machines]);

  const handleRefresh = () => {
    machines.forEach(machine => {
      dispatch(fetchTools({ machineId: machine.id }));
    });
  };

  const handleEditItem = (item: Tool) => {
    navigate(`/machines/${item.machine_id}?tab=tools`);
  };

  const handleDeleteRequest = (item: Tool) => {
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedItem) {
      await dispatch(deleteTool(selectedItem.id));
      setDeleteDialogOpen(false);
      setSelectedItem(null);
      handleRefresh();
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedItem(null);
  };

  const getMachineName = (machineId: string): string => {
    const machine = machines.find(m => m.id === machineId);
    return machine?.name || 'Unknown';
  };

  const handleExport = () => {
    const csvContent = [
      ['Name', 'Tool Number', 'Machine', 'Status', 'Condition', 'Location'].join(','),
      ...filteredTools.map(item => [
        `"${item.name}"`,
        item.tool_number || '',
        `"${getMachineName(item.machine_id)}"`,
        item.status,
        item.condition,
        item.location || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `werkzeuge_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const filteredTools = tools.filter(item => {
    if (!item) return false;

    const matchesSearch = !searchQuery ||
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.tool_number && item.tool_number.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    const matchesCondition = conditionFilter === 'all' || item.condition === conditionFilter;
    const matchesMachine = machineFilter === 'all' || item.machine_id === machineFilter;

    return matchesSearch && matchesStatus && matchesCondition && matchesMachine;
  });

  const availableCount = tools.filter(t => t.status === 'available').length;
  const inUseCount = tools.filter(t => t.status === 'in_use').length;
  const maintenanceCount = tools.filter(t => t.status === 'maintenance').length;
  const missingCount = tools.filter(t => t.status === 'missing').length;

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'tool_number',
      headerName: 'Werkzeugnummer',
      width: 150,
      valueFormatter: (value: any) => value || '-',
    },
    {
      field: 'machine_id',
      headerName: 'Maschine',
      width: 180,
      valueGetter: (value: any, row: any) => getMachineName(row.machine_id),
    },
    {
      field: 'location',
      headerName: 'Standort',
      width: 150,
      valueFormatter: (value: any) => value || '-',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      renderCell: (params) => (
        <StatusBadge type="tool-status" value={params.value} />
      ),
    },
    {
      field: 'condition',
      headerName: 'Zustand',
      width: 150,
      renderCell: (params) => (
        <StatusBadge type="tool-condition" value={params.value} />
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Aktionen',
      width: 100,
      getActions: (params: GridRowParams) => {
        const actions = [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Bearbeiten"
            onClick={() => handleEditItem(params.row as Tool)}
            disabled={!canEdit}
          />,
        ];
        if (canEdit) {
          actions.push(
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Löschen"
              onClick={() => handleDeleteRequest(params.row as Tool)}
            />
          );
        }
        return actions;
      },
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Werkzeuge Übersicht
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={handleExport}
            disabled={filteredTools.length === 0}
          >
            Export CSV
          </Button>
          <IconButton onClick={handleRefresh} title="Aktualisieren">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Status Summary */}
      <Box display="flex" gap={1} mb={3} flexWrap="wrap">
        <Chip label={`Verfügbar: ${availableCount}`} color="success" size="small" />
        <Chip label={`In Benutzung: ${inUseCount}`} color="primary" size="small" />
        <Chip label={`Wartung: ${maintenanceCount}`} color="warning" size="small" />
        {missingCount > 0 && <Chip label={`Vermisst: ${missingCount}`} color="error" size="small" />}
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <TextField
            placeholder="Werkzeuge durchsuchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">Alle Status</MenuItem>
              <MenuItem value="available">Verfügbar</MenuItem>
              <MenuItem value="in_use">In Benutzung</MenuItem>
              <MenuItem value="maintenance">Wartung</MenuItem>
              <MenuItem value="missing">Vermisst</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Zustand</InputLabel>
            <Select
              value={conditionFilter}
              label="Zustand"
              onChange={(e) => setConditionFilter(e.target.value)}
            >
              <MenuItem value="all">Alle Zustände</MenuItem>
              <MenuItem value="excellent">Ausgezeichnet</MenuItem>
              <MenuItem value="good">Gut</MenuItem>
              <MenuItem value="fair">Akzeptabel</MenuItem>
              <MenuItem value="poor">Schlecht</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Maschine</InputLabel>
            <Select
              value={machineFilter}
              label="Maschine"
              onChange={(e) => setMachineFilter(e.target.value)}
            >
              <MenuItem value="all">Alle Maschinen</MenuItem>
              {machines.map((machine) => (
                <MenuItem key={machine.id} value={machine.id}>
                  {machine.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Summary */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {filteredTools.length} von {tools.length} Werkzeug(e)
        </Typography>
      </Box>

      {/* Data Grid */}
      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={filteredTools}
          columns={columns}
          loading={isLoading}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
        />
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Werkzeug löschen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchten Sie "{selectedItem?.name}" wirklich löschen?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Abbrechen</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
